import { Observer } from "rxjs";
import jwt_decode from "jwt-decode";
import { PushNotificationService } from "./push-notification-service";

export class ProfileService {
  constructor(private observer: Observer<ProfileService>) { }

  public isLogged: boolean = false;
  public isPasswordResetRequired = false;
  public token: string;
  public userId: string;
  public firstName: string;
  public lastName: string;
  public login: string;
  public status: number = 0;
  public expirationDate: number;

  private readonly REFRESH_INTTERVAL = 30 * 1000;
  private refreshingSessionInterval: NodeJS.Timer;

  public async logIn(
    login: string,
    password: string
  ): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const token = await (window as any).validateCaptcha("login");
    const response = await fetch("b-profile/api/Session/login", {
      method: "POST",
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Recaptcha-Token': token,
      },
      body: JSON.stringify({
        emailAddress: login,
        password: password,
      }),
    });

    if (response.status >= 500) {
      return {
        isSuccess: false,
        errorMessage: "Nie udało się zalogować z powodu niedostępności usługi",
      };
    } else if (response.status == 403) {
      return {
        isSuccess: false,
        errorMessage: "To konto zostało zablokowane",
      };
    } else if (response.status == 400 || response.status == 401) {
      return {
        isSuccess: false,
        errorMessage: "Błędny login lub hasło",
      };
    } else if (response.status == 429) {
      return {
        isSuccess: false,
        errorMessage: "Zbyt dużo prób logowania. Proszę spróbować ponownie później.",
      };
    } else if (response.status == 409) {
      return {
        isSuccess: false,
        errorMessage: "Mechanizm weryfikacji użytkownika Google Recapcha zakwalifikował Twoje zachowania jako niezaufane. Prosimy o odświeżenie strony i powtórzenie wpisywania danych. Jeżeli problem będzie nadal występował, prosimy o ponowne uruchomienie przeglądarki, jej zmianę lub próbę na innym urządzeniu.",
      };
    } else if (!response.ok) {
      return {
        isSuccess: false,
        errorMessage: "Wystąpił nieznany błąd.",
      };
    }
    await this.createSessionFromResponse(response);
    this.startRefreshingSessionBackgroundJob();

    new PushNotificationService().addSubscription();
    window.location.href = "#/notification";
    return {
      isSuccess: true,
    };
  }

  public loadSession(): void {
    if (!window.localStorage["session"])
      return;
    const data = JSON.parse(window.localStorage["session"]);
    this.isLogged = data.isLogged;
    this.isPasswordResetRequired = data.isPasswordResetRequired;
    this.token = data.token;
    this.userId = data.userId;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.login = data.login;
    this.status = data.status;
    this.expirationDate = data.expirationDate;
    if (this.expirationDate < this.getDateNow()) {
      this.logOut();
    }
    else {
      this.observer.next(this);
      this.startRefreshingSessionBackgroundJob();
    }
  }

  public logOut(): void {
    window.localStorage.removeItem("session");
    window.location.href = "#";
    this.isLogged = false;
    this.isPasswordResetRequired = false;
    this.token = undefined;
    this.userId = undefined;
    this.firstName = undefined;
    this.lastName = undefined;
    this.login = undefined;
    this.status = undefined;
    this.expirationDate = undefined;

    if (this.refreshingSessionInterval != null)
      clearInterval(this.refreshingSessionInterval);
    this.observer.next(this);
  }

  private startRefreshingSessionBackgroundJob() {
    if (this.refreshingSessionInterval != null)
      clearInterval(this.refreshingSessionInterval);
    this.refreshingSessionInterval = setInterval(this.refreshSession.bind(this), this.REFRESH_INTTERVAL);
  }

  public async createSessionFromResponse(response: Response) {
    const body = (await response.json()) as {
      token: string;
      firstName: string;
      lastName: string;
      email?: string;
    };

    const token = jwt_decode<any>(body.token);

    this.isLogged = true;
    this.isPasswordResetRequired = token.pcr;
    this.token = body.token;
    this.userId = token.sub;
    this.firstName = body.firstName;
    this.lastName = body.lastName;
    this.login = body.email;
    this.status = token.status;
    this.expirationDate = token.exp;

    window.localStorage["session"] = JSON.stringify({
      isLogged: this.isLogged,
      isPasswordResetRequired: this.isPasswordResetRequired,
      token: this.token,
      userId: this.userId,
      firstName: this.firstName,
      lastName: this.lastName,
      login: this.login,
      status: this.status,
      expirationDate: this.expirationDate
    });
    this.observer.next(this);
  }

  private async refreshSession(): Promise<any> {
    if (!this.isLogged || this.expirationDate - this.getDateNow() < 0) {
      this.logOut();
    }

    const response = await fetch("b-profile/api/Session/refresh", {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${this.token}`
      }
    });

    if (response.status == 403) {
      this.logOut();
    }
    else if (!response.ok) {
      console.error(await response.json());
      return;
    }

    await this.createSessionFromResponse(response);
  }

  private getDateNow() {
    return Date.now() / 1000;
  }
}
